import { UserStatusEnum } from "@/domain/user.enum";
import { TFunction } from "i18next";
import * as yup from "yup";
import { InferType } from "yup";

const validationSchema = (
  t: TFunction,
  country: string,
  isEditMode: boolean = false,
) => {
  return yup.lazy(() => {
    const localeValidation = (): yup.StringSchema => {
      switch (country) {
        case "US":
          return yup
            .string()
            .test(
              "min",
              t("validations.validator_digits_min", { count: 10 }),
              (value) => {
                if (!value || value.length === 0) return true;
                return value.length >= 10;
              },
            )
            .test(
              "max",
              t("validations.validator_digits_max", { count: 10 }),
              (value) => {
                if (!value || value.length === 0) return true;
                return value.length <= 10;
              },
            )
            .test("phoneNumber", t("validations.invalid_format"), (value) => {
              if (!value || value.trim() === "") return true;
              const phoneRegex = /^\(?\d{3}\)?[-. ]?\d{3}[-. ]?\d{4}$/;
              return phoneRegex.test(value);
            });
        case "UK":
          return yup
            .string()
            .test(
              "min",
              t("validations.validator_digits_min", { count: 10 }),
              (value) => {
                if (!value || value.length === 0) return true;
                return value.length >= 10;
              },
            )
            .test(
              "max",
              t("validations.validator_digits_max", { count: 10 }),
              (value) => {
                if (!value || value.length === 0) return true;
                return value.length <= 10;
              },
            )
            .test("phoneNumber", t("validations.invalid_format"), (value) => {
              if (!value || value.trim() === "") return true;
              const phoneRegex = /^\(?\d{4}\)?[-. ]?\d{6}$/;
              return phoneRegex.test(value);
            });
        default:
          return yup.string();
      }
    };

    const assignedOrgUnitsSchema = yup.object({
      programId: yup.string().required(t("validations.required")),
      orgUnitIds: yup
        .array()
        .of(yup.string().required(t("validations.required")))
        .optional(),
    });

    const schemaFields: yup.ObjectShape = {
      firstName: yup
        .string()
        .required(t("validations.required"))
        .min(2, t("validations.min_length", { count: 2 }))
        .max(50, t("validations.max_length", { count: 50 })),
      lastName: yup
        .string()
        .required(t("validations.required"))
        .min(2, t("validations.min_length", { count: 2 }))
        .max(50, t("validations.max_length", { count: 50 })),
      email: yup
        .string()
        .required(t("validations.required"))
        .email(t("validations.invalid_format"))
        .max(100, t("validations.max_length", { count: 100 })),
      phoneNumber: localeValidation(),
      npi: yup
        .string()
        .optional()
        .test("max", t("validations.max_length", { count: 30 }), (value) => {
          if (!value || value.length === 0) return true;
          return value.length <= 30;
        }),
      roleId: yup.string().required("You must select one role for this user."),
      // AC04: assignedOrgUnits validation is conditional
      // Validation is handled in container for account admin/sysadmin/support exemption
      assignedOrgUnits: yup.array().of(assignedOrgUnitsSchema).optional(),
    };

    if (isEditMode) {
      schemaFields["status"] = yup
        .string()
        .oneOf(
          [
            UserStatusEnum.PENDING,
            UserStatusEnum.ACTIVE,
            UserStatusEnum.INACTIVE,
          ],
          t("validations.invalid_format"),
        );
    }

    return yup.object().shape(schemaFields);
  });
};

export type UserFormData = InferType<ReturnType<typeof validationSchema>>;

export default validationSchema;
